import React, { useState } from "react";
import { Group } from "@vx/group";
import { scaleTime, scaleLinear, scaleOrdinal, scalePoint } from "@vx/scale";
import styled, { withTheme } from "styled-components";
import { withTooltip, Tooltip } from "@vx/tooltip";
import { AxisBottom } from "@vx/axis";
import { LinePath, Circle } from "@vx/shape";
import { curveLinear } from "@vx/curve";
import { LegendItem, LegendOrdinal, LegendLabel } from "@vx/legend";
import { GridRows } from "@vx/grid";
import { ParentSize } from "@vx/responsive";
import moment from "moment";
import { localPoint } from "@vx/event";
import { useTranslation } from "react-i18next";
import { decimalPlaces, roundMealValue } from "../../../helpers/mealsHelper";

const LineChartWrapper = styled.div`
  height: 300px;
  display: flex;
  flex-direction: row;
`;
const StyledLegendLabel = styled(LegendLabel)`
  user-select: none;
  margin: 0 5px !important;
`;

const FixedPercentageAxis = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  margin-bottom: 54px;
  font-size: 11px;
`;

const StyledWrapper = styled.div`
  height: 310px;
  overflow-x: scroll;
  overflow-y: hidden;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  .description {
    padding-bottom: 10px;
    color: ${(props) => props.theme?.nero};
    font-size: 14px;
    user-select: none;
  }
  .legend {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    @media (max-width: 1100px) {
      flex-direction: column;
    }
  }
`;

const LabelsComponent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  @media (max-width: 647px) {
    flex-direction: column;
  }
`;

const LegendInfo = styled.div`
  user-select: none;
  display: flex;
  flex-direction: row;
  @media (max-width: 647px) {
    flex-direction: column;
  }
`;

const StudyLineChart = (props) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = props;

  const { t } = useTranslation();

  const [showProteinLine, setShowProteinLine] = useState(true);
  const [showFatsLine, setShowFatsnLine] = useState(true);
  const [showCarbohydratesLine, setShowCarbohydratesLine] = useState(true);
  const [showSugarsLine, setShowSugarsLine] = useState(true);
  const [showCaloriesLabels, setShowCaloriesLabels] = useState(true);
  const [legendList, setLegendList] = useState([
    "fats",
    "protein",
    "carbohydrates",
    "calories",
    "sugars",
  ]);
  const data = props.entries;
  const strokeWidth = 4;
  const yAxisValue = ["0", "20", "40", "60", "80", "100"];
  const ordinalColorScale = scaleOrdinal({
    domain: ["carbohydrates", "sugars", "protein", "fats", "calories"],
    range: [
      props.theme?.magentaPink,
      props.theme?.chiliRed,
      props.theme?.blueMagenta,
      props.theme?.aquamarine,
      props.theme?.geminiGray,
    ],
  });
  const tooltipStyles = {
    minWidth: 60,
    backgroundColor: "rgba(0,0,0,0.9)",
    color: props.theme?.white,
    userSelect: "none",
  };

  const handleCheckMark = (line) => {
    if (legendList.includes(line)) {
      setLegendList(legendList.filter((e) => e !== line));
    } else {
      setLegendList((legendList) => [...legendList, line]);
    }
  };

  const isCompleted = (entry) => {
    return entry.meals.find((meal) => meal.isCompleted);
  };

  const handleClickLegend = (line) => {
    handleCheckMark(line.datum);
    switch (line.datum) {
      case "protein":
        setShowProteinLine((prevState) => !prevState);
        break;
      case "fats":
        setShowFatsnLine((prevState) => !prevState);
        break;
      case "carbohydrates":
        setShowCarbohydratesLine((prevState) => !prevState);
        break;
      case "calories":
        setShowCaloriesLabels((prevState) => !prevState);
        break;
      case "sugars":
        setShowSugarsLine((prevState) => !prevState);
        break;
      default:
        break;
    }
  };

  const handleShowTooltip = (e, data, nMeals) => {
    const coords = localPoint(e.target.ownerSVGElement, e);
    const circleInfo = e.target.getBoundingClientRect();
    const leftAxis = document.getElementById("y-axis").getBoundingClientRect();
    showTooltip({
      tooltipData: { data, nMeals },
      tooltipLeft: circleInfo.left - leftAxis.left + 20,
      tooltipTop: coords.y - 20,
    });
  };

  const getSugarPercentage = (d) => {
    const result =
      (d.sugars.value * d.carbohydrates.percentage) / d.carbohydrates.value ||
      0;
    return result;
  };

  return (
    <>
      <ParentSize data-testid="study-line-chart">
        {({ width, height }) => {
          const x = (d) => moment(d.date);
          const protein = (d) => d.protein.percentage;
          const carbohydrates = (d) => d.carbohydrates.percentage;
          const fats = (d) => d.fats.percentage;
          const calories = data.map((a) =>
            Number(roundMealValue(a.calories.value))
              .toFixed(decimalPlaces)
              .toString()
          );
          const scrollableWidth = data.length * 100;
          const chartWidth = scrollableWidth < width ? width : scrollableWidth;
          const xMax = chartWidth - 120;
          const yMax = height - 60;
          const yScale = scaleLinear({
            rangeRound: [10, yMax],
            domain: [100, 0],
          });
          const xScale = scaleTime({
            rangeRound: [0, xMax],
            tickValues: data.map((d) => d.date),
            domain: [Math.min(...data.map(x)), Math.max(...data.map(x))],
          });
          const caloriesScale = scalePoint({
            range: [0, xMax],
            domain: calories.map((name, index) => 1000 + index + name),
          });
          return (
            <>
              <LineChartWrapper>
                <FixedPercentageAxis id="y-axis">
                  {yAxisValue.map((val, i) => (
                    <span key={`y${i}`}>{val}%</span>
                  ))}
                </FixedPercentageAxis>
                <StyledWrapper>
                  <svg width={chartWidth} height={height}>
                    <Group key={"ds"} left={30}>
                      <GridRows
                        scale={yScale}
                        width={chartWidth}
                        pointerEvents="none"
                        numTicks={4}
                      />
                      {showCaloriesLabels && (
                        <AxisBottom
                          tickLabelProps={() => ({
                            fill: props.theme?.geminiGray,
                            fontSize: 11,
                            textAnchor: "middle",
                          })}
                          hideAxisLine={true}
                          hideTicks={true}
                          tickFormat={(d) => d.substring(4) + " kcal"}
                          scale={caloriesScale}
                          labelOffset={15}
                          top={yMax + 5}
                        />
                      )}
                      <AxisBottom
                        tickLabelProps={() => ({
                          fontSize: 11,
                          fontWeight: 600,
                          textAnchor: "middle",
                        })}
                        hideAxisLine={true}
                        hideTicks={true}
                        tickFormat={(d) => moment(d).format("MMMM DD")}
                        scale={xScale}
                        numTicks={
                          data.length <= 3 ? data.length - 1 : data.length
                        }
                        labelOffset={15}
                        top={yMax + 25}
                      />
                      {showProteinLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(protein(d))}
                          stroke={props.theme?.blueMagenta}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showCarbohydratesLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(carbohydrates(d))}
                          stroke={props.theme?.magentaPink}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showFatsLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(fats(d))}
                          stroke={props.theme?.aquamarine}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showSugarsLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(getSugarPercentage(d))}
                          stroke={props.theme?.chiliRed}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {/* circles are the last elements to render so the tooltip can show properly */}
                      {showCarbohydratesLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`carbohydrates-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(carbohydrates(point))}
                              stroke={props.theme?.black}
                              fill={
                                isCompleted(point)
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.carbohydrates,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showProteinLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`protein-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(protein(point))}
                              stroke={props.theme?.black}
                              fill={
                                isCompleted(point)
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.protein,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showFatsLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`fats-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(fats(point))}
                              stroke={props.theme?.black}
                              fill={
                                isCompleted(point)
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.fats,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showSugarsLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`sugars-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(getSugarPercentage(point))}
                              stroke={props.theme?.black}
                              fill={
                                isCompleted(point)
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.sugars,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                    </Group>
                  </svg>
                  {tooltipOpen && (
                    <Tooltip
                      key={Math.random()}
                      top={tooltipTop}
                      left={tooltipLeft}
                      style={tooltipStyles}
                    >
                      <div>
                        <strong>
                          {`${roundMealValue(tooltipData.data.value)}${
                            tooltipData.data.unit
                          }${
                            tooltipData.data.percentage
                              ? ` - ${roundMealValue(
                                  tooltipData.data.percentage
                                )}%`
                              : ""
                          }`}
                        </strong>
                      </div>
                      <div>
                        <strong>{`${t("appText.dietDiary.meals")}: ${
                          tooltipData.nMeals
                        }`}</strong>
                      </div>
                    </Tooltip>
                  )}
                </StyledWrapper>
              </LineChartWrapper>
            </>
          );
        }}
      </ParentSize>
      <LegendWrapper>
        <span className="description">
          {`${t("appText.dietDiary.macronutrientsChart.help")}:`}
        </span>
        <div className="legend">
          <LegendOrdinal
            scale={ordinalColorScale}
            labelFormat={(label) =>
              `${label.charAt(0).toUpperCase()}${label.slice(1)}`
            }
          >
            {(labels) => (
              <LabelsComponent>
                {labels.map((label, i) => (
                  <LegendItem
                    key={`legend-quantile-${i}`}
                    margin="0 25px 0 0"
                    onClick={() => handleClickLegend(label)}
                  >
                    <svg width={20} height={20} style={{ cursor: "pointer" }}>
                      <g>
                        <rect fill={label.value} width={20} height={20} />
                        {legendList?.includes(label.datum) && (
                          <svg width="20" height="13" viewBox="0 0 20 20">
                            <path
                              fill={props.theme?.white}
                              d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"
                            />
                          </svg>
                        )}
                      </g>
                    </svg>
                    <StyledLegendLabel align="left" margin="0 0 0 4px">
                      {t(`appText.dietDiary.${label.datum}`)}
                    </StyledLegendLabel>
                  </LegendItem>
                ))}
              </LabelsComponent>
            )}
          </LegendOrdinal>
          <LegendInfo>
            <div className="mr-3">
              <svg
                height="12"
                width="12"
                style={{
                  marginRight: "5px",
                  marginBottom: "4px",
                }}
              >
                <Circle
                  cx="6"
                  cy="6"
                  r={5}
                  stroke={props.theme?.black}
                  fill={props.theme?.black}
                />
              </svg>
              <span>
                {t("appText.dietDiary.macronutrientsChart.completeDays")}
              </span>
            </div>
            <div>
              <svg
                height="12"
                width="12"
                style={{
                  marginRight: "5px",
                  marginBottom: "4px",
                }}
              >
                <Circle
                  cx="6"
                  cy="6"
                  r={5}
                  stroke={props.theme?.black}
                  fill={props.theme?.white}
                />
              </svg>
              <span>
                {t("appText.dietDiary.macronutrientsChart.notCompleteDays")}
              </span>
            </div>
          </LegendInfo>
        </div>
      </LegendWrapper>
    </>
  );
};

export default withTheme(withTooltip(StudyLineChart));
